import * as PIXI from 'pixi.js';
import { AudioSystem } from '../assets/AudioSystem';
import { TOWER_STATS, UPGRADE_A_STATS, UPGRADE_B_STATS, UPGRADE_C_STATS } from '../types/TowerTypes';
import { GAME_CONSTANTS } from '../utils/Constants';

interface TowerInfoPageOptions {
  onClose?: () => void;
  audioSystem?: AudioSystem | null;
}

export class TowerInfoPage {
  private container: PIXI.Container;
  private visible: boolean = false;
  private scrollContainer: PIXI.Container;
  private scrollY: number = 0;
  private maxScroll: number = 0;
  private options: TowerInfoPageOptions;

  constructor(options: TowerInfoPageOptions = {}) {
    this.options = options;
    this.container = new PIXI.Container();
    this.scrollContainer = new PIXI.Container();
    this.setupPage();
    this.container.visible = false;
  }

  private setupPage(): void {
    const screenWidth = 1920;
    const screenHeight = 1080;

    // Backdrop
    const backdrop = new PIXI.Graphics();
    backdrop.beginFill(0x000000, 0.95);
    backdrop.drawRect(0, 0, screenWidth, screenHeight);
    backdrop.endFill();
    backdrop.eventMode = 'static';
    backdrop.cursor = 'pointer';
    // Right-click to close
    backdrop.on('rightdown', () => {
      this.options.audioSystem?.playSound('button_click');
      this.hide();
    });
    this.container.addChild(backdrop);

    // Panel
    const panelWidth = 1400;
    const panelHeight = 900;
    const panelX = (screenWidth - panelWidth) / 2;
    const panelY = (screenHeight - panelHeight) / 2;

    const panel = new PIXI.Graphics();
    panel.beginFill(0x1a1a1a, 0.95);
    panel.drawRoundedRect(0, 0, panelWidth, panelHeight, 12);
    panel.endFill();
    panel.lineStyle(3, 0x444444, 0.8);
    panel.drawRoundedRect(0, 0, panelWidth, panelHeight, 12);
    panel.x = panelX;
    panel.y = panelY;
    panel.eventMode = 'static';
    this.container.addChild(panel);

    // Title
    const title = new PIXI.Text('Tower Information', {
      fontFamily: 'Courier New',
      fontSize: 48,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    title.x = panelX + 30;
    title.y = panelY + 30;
    this.container.addChild(title);

    // Scrollable area with mask
    const scrollMask = new PIXI.Graphics();
    scrollMask.beginFill(0x000000);
    scrollMask.drawRect(panelX + 30, panelY + 100, panelWidth - 60, panelHeight - 150);
    scrollMask.endFill();
    this.container.addChild(scrollMask);

    this.scrollContainer.x = panelX + 30;
    this.scrollContainer.y = panelY + 100;
    this.scrollContainer.mask = scrollMask;
    this.container.addChild(this.scrollContainer);

    let currentY = 0;
    const contentWidth = panelWidth - 60;

    // Arrow Tower
    currentY = this.createTowerSection('Arrow Tower', 'arrow', currentY, contentWidth);
    
    // Cannon Tower
    currentY = this.createTowerSection('Cannon Tower', 'cannon', currentY, contentWidth);
    
    // Sniper Tower
    currentY = this.createTowerSection('Sniper Tower', 'sniper', currentY, contentWidth);
    
    // Wall
    currentY = this.createWallSection(currentY, contentWidth);

    this.maxScroll = Math.max(0, currentY - (panelHeight - 150));

    // Scroll buttons
    const scrollUpBtn = new PIXI.Graphics();
    scrollUpBtn.beginFill(0x444444, 0.9);
    scrollUpBtn.drawRoundedRect(0, 0, 200, 40, 8);
    scrollUpBtn.endFill();
    scrollUpBtn.lineStyle(2, 0x888888);
    scrollUpBtn.drawRoundedRect(0, 0, 200, 40, 8);
    scrollUpBtn.x = panelX + (panelWidth - 200) / 2;
    scrollUpBtn.y = panelY + 50;
    scrollUpBtn.eventMode = 'static';
    scrollUpBtn.cursor = 'pointer';
    scrollUpBtn.on('pointerdown', () => {
      this.scrollY = Math.max(0, this.scrollY - 100);
      this.updateScroll();
    });
    this.container.addChild(scrollUpBtn);

    const scrollUpText = new PIXI.Text('▲ Scroll Up', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
      align: 'center',
    });
    scrollUpText.x = scrollUpBtn.x + 100 - scrollUpText.width / 2;
    scrollUpText.y = scrollUpBtn.y + 20 - scrollUpText.height / 2;
    this.container.addChild(scrollUpText);

    const scrollDownBtn = new PIXI.Graphics();
    scrollDownBtn.beginFill(0x444444, 0.9);
    scrollDownBtn.drawRoundedRect(0, 0, 200, 40, 8);
    scrollDownBtn.endFill();
    scrollDownBtn.lineStyle(2, 0x888888);
    scrollDownBtn.drawRoundedRect(0, 0, 200, 40, 8);
    scrollDownBtn.x = panelX + (panelWidth - 200) / 2;
    scrollDownBtn.y = panelY + panelHeight - 50;
    scrollDownBtn.eventMode = 'static';
    scrollDownBtn.cursor = 'pointer';
    scrollDownBtn.on('pointerdown', () => {
      this.scrollY = Math.min(this.maxScroll, this.scrollY + 100);
      this.updateScroll();
    });
    this.container.addChild(scrollDownBtn);

    const scrollDownText = new PIXI.Text('▼ Scroll Down', {
      fontFamily: 'Courier New',
      fontSize: 18,
      fill: 0xffffff,
      align: 'center',
    });
    scrollDownText.x = scrollDownBtn.x + 100 - scrollDownText.width / 2;
    scrollDownText.y = scrollDownBtn.y + 20 - scrollDownText.height / 2;
    this.container.addChild(scrollDownText);

    // Close button
    const closeButton = new PIXI.Graphics();
    closeButton.beginFill(0x006600, 0.9);
    closeButton.drawRoundedRect(0, 0, 250, 50, 8);
    closeButton.endFill();
    closeButton.lineStyle(2, 0x00aa00);
    closeButton.drawRoundedRect(0, 0, 250, 50, 8);
    closeButton.x = panelX + panelWidth - 280;
    closeButton.y = panelY + panelHeight - 70;
    closeButton.eventMode = 'static';
    closeButton.cursor = 'pointer';
    closeButton.on('pointerdown', () => {
      this.options.audioSystem?.playSound('button_click');
      this.hide();
    });
    this.container.addChild(closeButton);

    const closeText = new PIXI.Text('Close', {
      fontFamily: 'Courier New',
      fontSize: 24,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    closeText.x = closeButton.x + closeButton.width / 2 - closeText.width / 2;
    closeText.y = closeButton.y + closeButton.height / 2 - closeText.height / 2;
    this.container.addChild(closeText);
  }

  private createTowerSection(name: string, type: 'arrow' | 'cannon' | 'sniper', y: number, width: number): number {
    const stats = TOWER_STATS[type];
    const upgradeA = UPGRADE_A_STATS[type];
    const upgradeB = UPGRADE_B_STATS[type];
    const upgradeC = UPGRADE_C_STATS[type];

    // Section header
    const header = new PIXI.Graphics();
    header.beginFill(0x2a2a2a, 0.9);
    header.drawRect(0, y, width, 50);
    header.endFill();
    header.lineStyle(2, 0x666666);
    header.drawRect(0, y, width, 50);
    this.scrollContainer.addChild(header);

    const headerText = new PIXI.Text(name, {
      fontFamily: 'Courier New',
      fontSize: 36,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    headerText.x = 15;
    headerText.y = y + 12;
    this.scrollContainer.addChild(headerText);
    y += 60;

    // Base stats
    const baseStatsText = new PIXI.Text(
      `Base Stats:\n` +
      `Cost: ${stats.cost}g | HP: ${stats.hp} | Damage: ${stats.damage}\n` +
      `Range: ${stats.range} tiles | Speed: ${stats.attackSpeed.toFixed(2)}/s` +
      (stats.aoeRadius ? ` | AoE: ${stats.aoeRadius} tiles` : ''),
      {
        fontFamily: 'Courier New',
        fontSize: 24,
        fill: 0xcccccc,
        wordWrap: true,
        wordWrapWidth: width - 30,
        lineHeight: 32,
      }
    );
    baseStatsText.x = 15;
    baseStatsText.y = y;
    this.scrollContainer.addChild(baseStatsText);
    y += baseStatsText.height + 20;

    // Description
    const descriptions: Record<string, string> = {
      arrow: 'Single target physical damage. Fast attack speed, excellent for consistent DPS against individual enemies.',
      cannon: 'Area of Effect physical damage. Reduces enemy armor, making it perfect for groups and supporting other towers.',
      sniper: 'Long range single target. High damage per shot, slow attack speed. True Strike ability means attacks never miss.',
    };

    const descText = new PIXI.Text(`Description: ${descriptions[type]}`, {
      fontFamily: 'Courier New',
      fontSize: 22,
      fill: 0xaaaaaa,
      wordWrap: true,
      wordWrapWidth: width - 30,
      lineHeight: 30,
    });
    descText.x = 15;
    descText.y = y;
    this.scrollContainer.addChild(descText);
    y += descText.height + 25;

    // Upgrade A
    const upgradeAHeader = new PIXI.Text(`Upgrade A (${upgradeA.cost}g):`, {
      fontFamily: 'Courier New',
      fontSize: 28,
      fill: 0xffd700,
      fontWeight: 'bold',
    });
    upgradeAHeader.x = 15;
    upgradeAHeader.y = y;
    this.scrollContainer.addChild(upgradeAHeader);
    y += 35;

    let upgradeAText = '';
    if (upgradeA.damage) {
      const newDamage = upgradeA.damage;
      const increase = newDamage - stats.damage;
      upgradeAText += `Damage: ${newDamage} (+${increase})\n`;
    }
    if (upgradeA.attackSpeed) {
      upgradeAText += `Speed: ${upgradeA.attackSpeed.toFixed(2)}/s\n`;
    }
    if (upgradeA.range) upgradeAText += `Range: ${stats.range + upgradeA.range} tiles\n`;
    if (upgradeA.aoeRadius) upgradeAText += `AoE: ${upgradeA.aoeRadius} tiles\n`;
    if (upgradeA.special) upgradeAText += `Special: ${upgradeA.special}`;

    const upgradeATextObj = new PIXI.Text(upgradeAText.trim(), {
      fontFamily: 'Courier New',
      fontSize: 22,
      fill: 0xcccccc,
      wordWrap: true,
      wordWrapWidth: width - 45,
      lineHeight: 30,
    });
    upgradeATextObj.x = 30;
    upgradeATextObj.y = y;
    this.scrollContainer.addChild(upgradeATextObj);
    y += upgradeATextObj.height + 20;

    // Upgrade B
    const upgradeBHeader = new PIXI.Text(`Upgrade B (${upgradeB.cost}g):`, {
      fontFamily: 'Courier New',
      fontSize: 28,
      fill: 0xffd700,
      fontWeight: 'bold',
    });
    upgradeBHeader.x = 15;
    upgradeBHeader.y = y;
    this.scrollContainer.addChild(upgradeBHeader);
    y += 35;

    let upgradeBText = '';
    if (upgradeB.damage) {
      const baseDamage = upgradeA.damage || stats.damage;
      const newDamage = upgradeB.damage;
      const increase = newDamage - baseDamage;
      upgradeBText += `Damage: ${newDamage} (+${increase})\n`;
    }
    if (upgradeB.attackSpeed) {
      upgradeBText += `Speed: ${upgradeB.attackSpeed.toFixed(2)}/s\n`;
    }
    if (upgradeB.range) {
      const baseRange = stats.range + (upgradeA.range || 0);
      upgradeBText += `Range: ${baseRange + upgradeB.range} tiles\n`;
    }
    if (upgradeB.special) upgradeBText += `Special: ${upgradeB.special}`;

    const upgradeBTextObj = new PIXI.Text(upgradeBText.trim(), {
      fontFamily: 'Courier New',
      fontSize: 22,
      fill: 0xcccccc,
      wordWrap: true,
      wordWrapWidth: width - 45,
      lineHeight: 30,
    });
    upgradeBTextObj.x = 30;
    upgradeBTextObj.y = y;
    this.scrollContainer.addChild(upgradeBTextObj);
    y += upgradeBTextObj.height + 25;

    // Upgrade C
    const upgradeCHeader = new PIXI.Text(`Upgrade C (${upgradeC.cost}g):`, {
      fontFamily: 'Courier New',
      fontSize: 28,
      fill: 0xff00ff,
      fontWeight: 'bold',
    });
    upgradeCHeader.x = 15;
    upgradeCHeader.y = y;
    this.scrollContainer.addChild(upgradeCHeader);
    y += 35;

    let upgradeCText = '';
    if (upgradeC.damage) {
      const baseDamage = upgradeB.damage || upgradeA.damage || stats.damage;
      const newDamage = baseDamage + upgradeC.damage;
      upgradeCText += `Damage: ${newDamage} (+${upgradeC.damage})\n`;
    }
    if (upgradeC.attackSpeed) {
      upgradeCText += `Speed: ${upgradeC.attackSpeed.toFixed(2)}/s\n`;
    }
    if (upgradeC.range) {
      const baseRange = stats.range + (upgradeA.range || 0) + (upgradeB.range || 0);
      upgradeCText += `Range: ${baseRange + upgradeC.range} tiles\n`;
    }
    if (upgradeC.special) upgradeCText += `Special: ${upgradeC.special}`;

    const upgradeCTextObj = new PIXI.Text(upgradeCText.trim(), {
      fontFamily: 'Courier New',
      fontSize: 22,
      fill: 0xcccccc,
      wordWrap: true,
      wordWrapWidth: width - 45,
      lineHeight: 30,
    });
    upgradeCTextObj.x = 30;
    upgradeCTextObj.y = y;
    this.scrollContainer.addChild(upgradeCTextObj);
    y += upgradeCTextObj.height + 30;

    // Interactions
    const interactionsText = new PIXI.Text(
      `Interactions:\n` +
      `• Can be placed on walls (Quick Build enabled)\n` +
      `• Can be sold for 50% of total cost (40% if damaged)\n` +
      `• Can be repaired (costs 5% per 10% missing HP, max 45%)\n` +
      `• Towers are disabled during repair\n` +
      `• Upgrades are cumulative (A → B → C)`,
      {
        fontFamily: 'Courier New',
        fontSize: 20,
        fill: 0x88ccff,
        wordWrap: true,
        wordWrapWidth: width - 30,
        lineHeight: 28,
      }
    );
    interactionsText.x = 15;
    interactionsText.y = y;
    this.scrollContainer.addChild(interactionsText);
    y += interactionsText.height + 40;

    return y;
  }

  private createWallSection(y: number, width: number): number {
    // Section header
    const header = new PIXI.Graphics();
    header.beginFill(0x2a2a2a, 0.9);
    header.drawRect(0, y, width, 50);
    header.endFill();
    header.lineStyle(2, 0x666666);
    header.drawRect(0, y, width, 50);
    this.scrollContainer.addChild(header);

    const headerText = new PIXI.Text('Wall', {
      fontFamily: 'Courier New',
      fontSize: 36,
      fill: 0xffffff,
      fontWeight: 'bold',
    });
    headerText.x = 15;
    headerText.y = y + 12;
    this.scrollContainer.addChild(headerText);
    y += 60;

    // Base stats
    const baseStatsText = new PIXI.Text(
      `Base Stats:\n` +
      `Cost: ${GAME_CONSTANTS.WALL_COST}g | HP: ${GAME_CONSTANTS.WALL_HP_BASE}\n` +
      `Blocks enemy path`,
      {
        fontFamily: 'Courier New',
        fontSize: 24,
        fill: 0xcccccc,
        wordWrap: true,
        wordWrapWidth: width - 30,
        lineHeight: 32,
      }
    );
    baseStatsText.x = 15;
    baseStatsText.y = y;
    this.scrollContainer.addChild(baseStatsText);
    y += baseStatsText.height + 20;

    // Description
    const descText = new PIXI.Text(
      `Description: Blocks enemy movement. Essential for creating mazes and funneling enemies through kill zones. Strategic placement is key to maximizing tower effectiveness.`,
      {
        fontFamily: 'Courier New',
        fontSize: 22,
        fill: 0xaaaaaa,
        wordWrap: true,
        wordWrapWidth: width - 30,
        lineHeight: 30,
      }
    );
    descText.x = 15;
    descText.y = y;
    this.scrollContainer.addChild(descText);
    y += descText.height + 25;

    // Interactions
    const interactionsText = new PIXI.Text(
      `Interactions:\n` +
      `• Can be placed on empty tiles\n` +
      `• Can be sold for 50% of cost (40% if damaged)\n` +
      `• Can be repaired (costs 5% per 10% missing HP, max 45%)\n` +
      `• Towers can be built on top (Quick Build enabled)\n` +
      `• Enemies will break through if path is blocked (Block Breaker mechanic)`,
      {
        fontFamily: 'Courier New',
        fontSize: 20,
        fill: 0x88ccff,
        wordWrap: true,
        wordWrapWidth: width - 30,
        lineHeight: 28,
      }
    );
    interactionsText.x = 15;
    interactionsText.y = y;
    this.scrollContainer.addChild(interactionsText);
    y += interactionsText.height + 40;

    return y;
  }

  private updateScroll(): void {
    // Scroll container position is set relative to panel
    const panelY = (1080 - 900) / 2;
    this.scrollContainer.y = panelY + 100 - this.scrollY;
  }

  show(): void {
    this.visible = true;
    this.container.visible = true;
    this.scrollY = 0;
    this.updateScroll();
    // Enable pointer events on the info pages canvas
    if ((window as any).infoPagesCanvas) {
      (window as any).infoPagesCanvas.style.pointerEvents = 'auto';
    }
  }

  hide(): void {
    this.visible = false;
    this.container.visible = false;
    // Disable pointer events when hidden
    const infoPagesCanvas = (window as any).infoPagesCanvas;
    if (infoPagesCanvas) {
      infoPagesCanvas.style.pointerEvents = 'none';
    }
    this.options.onClose?.();
  }

  getContainer(): PIXI.Container {
    return this.container;
  }
}

